import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { FormInputText } from '../../../../inputs/FormInputText';
import { ButtonClassNames, InputRegexPattern } from '../../../../../models/Common';
import { CustomMUIButton } from '../../../buttons/CustomMUIButton';
import { FormInputPassword } from '../../../../inputs/FormInputPassword';
import { macAddressExample } from '../../../../../constants/configuration';
import { iptvApi } from '../../../../../services/apiServices/user/iptv';
import { useCustomSnackbar } from '../../../../../hooks/useCustomEnqueueSnackbar';
import { getErrorMessage } from '../../../../../utils';

interface IptvDeviceForm {
  editedLogin: string;
  editedMac: string;
  editedPassword: string;
  passwordConfirmation: string;
}

interface EditIptvDeviceProps {
  handleClose: () => void;
  login: string;
  deviceId: number;
  accountId: string | number;
  macAddress: string;
}

export const EditIptvDevice: FC<EditIptvDeviceProps> = ({ login, deviceId, accountId, macAddress, handleClose }) => {
  const { t } = useTranslation();
  const methods = useForm<IptvDeviceForm>({ defaultValues: { editedMac: macAddress, editedLogin: login } });
  const { handleSubmit, watch, register } = methods;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [editIptvDevice, { isLoading }] = iptvApi.useEditDeviceMutation();

  const password = useRef({});
  password.current = watch('editedPassword', '');

  const passwordConfirmRule = {
    validate: (value = '') => value === password.current || t('validation.passwordsDoNotMatch').toString(),
  };

  const handleEditDevice = async ({ editedLogin, editedMac, editedPassword }: IptvDeviceForm) => {
    try {
      await editIptvDevice({
        accountId,
        deviceId,
        ...(!!editedMac && { mac: editedMac }),
        ...(!!editedLogin && { login: editedLogin }),
        ...(!!editedPassword && { password: editedPassword }),
      })
        .unwrap()
        .then(() => {
          customEnqueueSnackbar(t('snackbar.dataUpdated'), 'success');
          handleClose();
        });
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{t('modals.changingIptvDevices')}</article>
      </div>
      <FormProvider {...methods}>
        <form className="modals__form" onSubmit={handleSubmit(handleEditDevice)}>
          <FormInputText
            name="editedLogin"
            className="primary"
            label={t('common.login')}
            helperText={t('input.numberOfCharactersInLowerCaseNumbersSigns', { from: 3, to: 16 })}
            isRequired
            minLength={3}
            maxLength={16}
            pattern={InputRegexPattern.InternetLogin}
          />
          <FormInputText
            name="editedMac"
            className="primary"
            label={t('input.macAddress')}
            helperText={t('input.example', { val: macAddressExample })}
            isRequired
            minLength={17}
            maxLength={17}
            pattern={InputRegexPattern.MacAddress}
          />
          <FormInputPassword
            name="editedPassword"
            label={t('auth.newPassword')}
            className="primary"
            ref={(e) => register('editedPassword', { value: e?.value || '' })}
            helperText={t('input.numberOfCharactersInLatinNumbers_', { from: 6, to: 16 })}
            pattern={/^[a-z0-9_]*$/i}
            minLength={6}
            maxLength={16}
          />
          <FormInputPassword
            name="passwordConfirmation"
            label={t('auth.repeatNewPassword')}
            ref={() => register('passwordConfirmation', passwordConfirmRule)}
            className="primary"
          />
          <div className="modals__btn-group">
            <CustomMUIButton
              label={t('buttons.save')}
              type="submit"
              className={ButtonClassNames.PrimaryFulfilled}
              isBold
              isLoading={isLoading}
            />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
